// First-party analytics — fire-and-forget POST to /api/analytics/track.
//
// Never throws, never blocks. If the request fails we drop it silently
// rather than surface an error UI — analytics failures shouldn't ever
// touch the user. Debounce/batch is intentionally NOT built here; the
// server-side writes are cheap and the volume is low.
//
// Wire this in from route effects (usePageView) or specific action
// handlers (e.g. export button onClick). Available events are listed in
// the ANALYTICS_EVENTS set in server.ts — the server drops unknowns.
import { useEffect } from "react";
import { useLocation } from "wouter";
import type { Persona } from "@release-hub/shared";
import { api } from "@/api/client";
import { usePersona } from "@/auth/Persona";

export type TrackEvent =
  | "page_view"
  | "export"
  | "revert"
  | "search"
  | "history_open"
  | "publish"
  | "unpublish";

export interface TrackPayload {
  event: TrackEvent;
  path?: string;
  persona?: Persona;
  entity_type?: string;
  entity_id?: string;
  meta?: Record<string, unknown>;
}

/** Fire a single event. Safe to call from render (side-effect swallowed). */
export function track(payload: TrackPayload): void {
  const body: Record<string, unknown> = { event: payload.event };
  if (payload.path) body.path = payload.path;
  if (payload.persona) body.persona = payload.persona;
  if (payload.entity_type) body.entity_type = payload.entity_type;
  if (payload.entity_id) body.entity_id = payload.entity_id;
  if (payload.meta) body.meta = payload.meta;
  // Fire-and-forget — catch to swallow network / 4xx errors so analytics
  // never breaks user flow. In prod these fail rarely; in dev they might
  // fail when the server is restarting.
  api("/api/analytics/track", { method: "POST", body }).catch(() => {});
}

/** Hook: fire a track() with the current persona baked in. Consumers
 *  don't need to import usePersona themselves. */
export function useTrack() {
  const { persona } = usePersona();
  return (payload: Omit<TrackPayload, "persona">) =>
    track({ ...payload, persona });
}

/** Fires a `page_view` event whenever the wouter location changes. Mount
 *  once at the app root so every navigation gets recorded. */
export function usePageViewTracker() {
  const [location] = useLocation();
  const { persona } = usePersona();
  useEffect(() => {
    track({ event: "page_view", path: location, persona });
  }, [location, persona]);
}
